'use client';

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(7);
    const newToast = { ...toast, id };

    setToasts((prev) => [...prev, newToast]);

    // Auto-remove after duration
    const duration = toast.duration ?? 4000;
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const success = useCallback(
    (title: string, message?: string) => {
      addToast({ type: 'success', title, message });
    },
    [addToast]
  );

  const error = useCallback(
    (title: string, message?: string) => {
      addToast({ type: 'error', title, message });
    },
    [addToast]
  );

  const warning = useCallback(
    (title: string, message?: string) => {
      addToast({ type: 'warning', title, message });
    },
    [addToast]
  );

  const info = useCallback(
    (title: string, message?: string) => {
      addToast({ type: 'info', title, message });
    },
    [addToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// Toast Container
const ToastContainer = ({
  toasts,
  onRemove,
}: {
  toasts: Toast[];
  onRemove: (id: string) => void;
}) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-0 right-0 z-50 flex flex-col gap-2 p-4 max-w-sm w-full pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={() => onRemove(toast.id)} />
      ))}
    </div>
  );
};

// Individual Toast
const ToastItem = ({ toast, onRemove }: { toast: Toast; onRemove: () => void }) => {
  const config = {
    success: {
      icon: <CheckCircle className="h-4 w-4" />,
      iconColor: 'text-success-500',
      bg: 'bg-success-50 dark:bg-success-500/10',
      border: 'border-success-200 dark:border-success-500/20',
      accent: 'bg-success-500',
    },
    error: {
      icon: <AlertCircle className="h-4 w-4" />,
      iconColor: 'text-danger-500',
      bg: 'bg-danger-50 dark:bg-danger-500/10',
      border: 'border-danger-200 dark:border-danger-500/20',
      accent: 'bg-danger-500',
    },
    warning: {
      icon: <AlertTriangle className="h-4 w-4" />,
      iconColor: 'text-warning-500',
      bg: 'bg-warning-50 dark:bg-warning-500/10',
      border: 'border-warning-200 dark:border-warning-500/20',
      accent: 'bg-warning-500',
    },
    info: {
      icon: <Info className="h-4 w-4" />,
      iconColor: 'text-primary-500',
      bg: 'bg-primary-50 dark:bg-primary-500/10',
      border: 'border-primary-200 dark:border-primary-500/20',
      accent: 'bg-primary-500',
    },
  };

  const { icon, iconColor, bg, border, accent } = config[toast.type];

  return (
    <div
      className={cn(
        'pointer-events-auto relative overflow-hidden',
        'flex items-start gap-3 rounded-lg border p-3',
        'shadow-lg dark:shadow-[0_4px_20px_rgba(0,0,0,0.3)]',
        'animate-slideUp',
        bg,
        border
      )}
    >
      {/* Accent bar */}
      <div className={cn('absolute left-0 top-0 bottom-0 w-0.5', accent)} />

      <span className={cn('shrink-0 mt-0.5', iconColor)}>{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-secondary-100">
          {toast.title}
        </p>
        {toast.message && (
          <p className="mt-0.5 text-xs text-gray-600 dark:text-secondary-400">
            {toast.message}
          </p>
        )}
      </div>
      <button
        onClick={onRemove}
        className="shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300 transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
